/**
 * Laws Compliance Panel
 *
 * Displays Platform Laws compliance status with expandable sections,
 * check details, remediation suggestions, and audit trigger.
 *
 * Phase 3.5-C: Operational Intelligence
 */

import React, { useState } from 'react';
import type {
  LawsComplianceState,
  LawComplianceDetail,
  ComplianceCheck,
  ComplianceStatus,
  CheckStatus,
} from '../../../features/dashboard/types/compliance';
import {
  ShieldIcon,
  CheckCircleIcon,
  AlertIcon,
  CloseIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  RefreshIcon,
} from '../../common/Icons';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<ComplianceStatus, string> = {
  compliant: '#4ade80',
  warning: '#fbbf24',
  violation: '#f87171',
};

const CHECK_STATUS_COLORS: Record<CheckStatus, string> = {
  pass: '#4ade80',
  warning: '#fbbf24',
  fail: '#f87171',
};

const CHECK_STATUS_ICONS: Record<CheckStatus, React.FC<{ size?: number; style?: React.CSSProperties }>> = {
  pass: CheckCircleIcon,
  warning: AlertIcon,
  fail: CloseIcon,
};

const LAW_LABELS: Record<string, string> = {
  zeroth: 'Zeroth',
  first: 'First',
  second: 'Second',
  third: 'Third',
};

function scoreColor(score: number): string {
  if (score >= 90) return '#4ade80';
  if (score >= 70) return '#fbbf24';
  return '#f87171';
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ScoreGauge: React.FC<{ score: number; status: ComplianceStatus }> = ({ score, status }) => {
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: '96px', height: '96px' }}>
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r="40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
        <circle
          cx="48"
          cy="48"
          r="40"
          fill="none"
          stroke={STATUS_COLORS[status]}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 48 48)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: '22px', fontWeight: 700, color: STATUS_COLORS[status] }}>{score}</span>
        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '-2px' }}>/ 100</span>
      </div>
    </div>
  );
};

const CheckItem: React.FC<{ check: ComplianceCheck }> = ({ check }) => {
  const [expanded, setExpanded] = useState(false);
  const StatusIcon = CHECK_STATUS_ICONS[check.status];

  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: '6px',
        background: check.status !== 'pass' ? `${CHECK_STATUS_COLORS[check.status]}08` : 'transparent',
        cursor: check.remediation ? 'pointer' : 'default',
      }}
      onClick={() => check.remediation && setExpanded(!expanded)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <StatusIcon size={14} style={{ color: CHECK_STATUS_COLORS[check.status], flexShrink: 0 }} />
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', flex: 1 }}>{check.name}</span>
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
          {check.status === 'pass' ? 'Pass' : check.status === 'warning' ? 'Warning' : 'Fail'}
        </span>
      </div>
      <p style={{ margin: '2px 0 0 22px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', lineHeight: '15px' }}>
        {check.details}
      </p>
      {expanded && check.remediation && (
        <div
          style={{
            margin: '6px 0 0 22px',
            padding: '6px 8px',
            background: 'rgba(251,191,36,0.06)',
            border: '1px solid rgba(251,191,36,0.12)',
            borderRadius: '4px',
            fontSize: '11px',
            color: 'rgba(251,191,36,0.8)',
            lineHeight: '15px',
          }}
        >
          <strong>Remediation:</strong> {check.remediation}
        </div>
      )}
    </div>
  );
};

const LawSection: React.FC<{ lawKey: string; detail: LawComplianceDetail }> = ({ lawKey, detail }) => {
  const [expanded, setExpanded] = useState(detail.status !== 'compliant');

  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          padding: '10px 12px',
          background: 'rgba(255,255,255,0.02)',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {expanded ? (
          <ChevronDownIcon size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
        ) : (
          <ChevronRightIcon size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
        )}

        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: STATUS_COLORS[detail.status],
            flexShrink: 0,
          }}
        />

        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.85)' }}>
            {LAW_LABELS[lawKey] || lawKey} Law
          </span>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginLeft: '8px' }}>
            {detail.description}
          </span>
        </div>

        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: scoreColor(detail.score),
            minWidth: '32px',
            textAlign: 'right',
          }}
        >
          {detail.score}%
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '4px 8px 8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {detail.checks.map((check) => (
            <CheckItem key={check.id} check={check} />
          ))}
          <div
            style={{
              padding: '4px 10px',
              fontSize: '10px',
              color: 'rgba(255,255,255,0.25)',
              textAlign: 'right',
            }}
          >
            Last checked: {formatTime(detail.last_checked)}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export interface LawsCompliancePanelProps {
  data: LawsComplianceState | null;
  isLoading: boolean;
  onRunAudit: () => void;
  isAuditing: boolean;
}

export const LawsCompliancePanel: React.FC<LawsCompliancePanelProps> = ({
  data,
  isLoading,
  onRunAudit,
  isAuditing,
}) => {
  if (isLoading && !data) {
    return (
      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.3)',
          fontSize: '13px',
        }}
      >
        Loading compliance data...
      </div>
    );
  }

  if (!data) return null;

  const lawOrder = ['zeroth', 'first', 'second', 'third'];

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShieldIcon size={16} style={{ color: 'rgba(255,255,255,0.5)' }} />
          <span style={{ fontWeight: 600, fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
            Platform Laws Compliance
          </span>
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={onRunAudit}
            disabled={isAuditing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '11px',
              background: isAuditing ? 'rgba(255,255,255,0.04)' : 'rgba(96,165,250,0.1)',
              border: '1px solid rgba(96,165,250,0.2)',
              borderRadius: '6px',
              color: isAuditing ? 'rgba(255,255,255,0.3)' : 'rgba(96,165,250,0.9)',
              cursor: isAuditing ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshIcon size={12} />
            {isAuditing ? 'Auditing...' : 'Run Audit'}
          </button>
        </div>
      </div>

      {/* Score Summary */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <ScoreGauge score={data.overall_score} status={data.overall_status} />

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
            Overall:{' '}
            <span style={{ color: STATUS_COLORS[data.overall_status], fontWeight: 500 }}>
              {data.overall_status === 'compliant' ? 'Compliant' : data.overall_status === 'warning' ? 'Warnings' : 'Violations'}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {lawOrder.map((key) => {
              const detail = data.laws[key];
              if (!detail) return null;
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: STATUS_COLORS[detail.status],
                    }}
                  />
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                    {LAW_LABELS[key]}: {detail.score}%
                  </span>
                </div>
              );
            })}
          </div>

          {data.recent_violations.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: 'rgba(251,191,36,0.7)' }}>
              {data.recent_violations.length} active {data.recent_violations.length === 1 ? 'issue' : 'issues'} requiring attention
            </div>
          )}
        </div>
      </div>

      {/* Law Sections */}
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {lawOrder.map((key) => {
          const detail = data.laws[key];
          if (!detail) return null;
          return <LawSection key={key} lawKey={key} detail={detail} />;
        })}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '8px 16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '10px',
          color: 'rgba(255,255,255,0.25)',
        }}
      >
        <span>
          Last audit: {data.last_audit.completed_at ? formatTime(data.last_audit.completed_at) : 'N/A'}
          {data.last_audit.duration_seconds != null && ` (${data.last_audit.duration_seconds.toFixed(1)}s)`}
        </span>
        <span>{data.last_audit.checks_run} checks run</span>
      </div>
    </div>
  );
};

export default LawsCompliancePanel;
